import { Form, FormikProvider, useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Box, Flex } from 'reflexbox';

import {
	useCompleteWorkspaceCheckpoint,
	useErrorBoundary,
	useFeature,
	useLocalize,
	useViewerRoles,
} from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import {
	ArchiveIcon,
	Button,
	FlexGap,
	GraphQLErrorMessage,
	Heading,
	Label,
	QuillText,
	Spinner,
	tokens,
	useToasts,
} from '@treams/treams-ui';

import { formatSalaryScale } from '../../../job/formatSalaryScale';
import { GrowthPathExclusionToggle } from '../../../job/GrowthPathExclusionToggle';
import { KeyPositionToggle } from '../../../job/KeyPositionToggle';
import DetailsTableForm from '../../../packages/kakapo-ui-kit/components/common/DetailsTableForm/DetailsTableForm';
import { DetailsTableFormValueTypes } from '../../../packages/kakapo-ui-kit/components/common/DetailsTableForm/DetailsTableFormValue';
import GoalTemplates from './goal-templates/GoalTemplates';
import JobCompetenciesEditor from './JobCompetenciesEditor';

export function JobTitleEditor(props: { jobTitle?: graphql.JobTitle }) {
	const { t, i18n } = useTranslation();
	const { localize } = useLocalize();
	const [error, attempt] = useErrorBoundary();
	const { addToast } = useToasts();
	const history = useHistory();
	const { isHRBusinessPartner } = useViewerRoles();

	const showJobCompetencies = useFeature(graphql.FeatureName.JobCompetencies);
	const showJobGoalTemplates = useFeature(graphql.FeatureName.JobGoalTemplates);
	const hasRewards = useFeature(graphql.FeatureName.Rewards);

	const [create, { loading: createLoading }] = graphql.useCreateJobTitleMutation();
	const [update, { loading: updateLoading }] = graphql.useUpdateJobTitleMutation();
	const [archive, { loading: archiveLoading }] = graphql.useArchiveJobTitleMutation();
	const [restore, { loading: restoreLoading }] = graphql.useRestoreJobTitleMutation();
	const complete = useCompleteWorkspaceCheckpoint('workspace.settings.jobTitles');

	const { data: salaryScalesData } = graphql.useSalaryScalesQuery({
		variables: { isArchived: false, first: 1000 },
		skip: isHRBusinessPartner || !hasRewards,
		fetchPolicy: 'cache-and-network',
	});

	const formik = useFormik({
		initialValues: {
			title: props.jobTitle?.title ?? [],
			description: props.jobTitle?.description ?? [],
			externalReferenceID: props.jobTitle?.externalReferenceID,
			salaryScaleID: props.jobTitle?.salaryScale?.id ?? '',
		},
		onSubmit: attempt(async () => {
			const salaryScale = hasRewards ? { salaryScaleID: formik.values.salaryScaleID || null } : {};

			if (props.jobTitle) {
				await update({
					variables: {
						input: {
							id: props.jobTitle.id,
							title: formik.values.title,
							description: formik.values.description,
							externalReferenceID: formik.values.externalReferenceID,
							...salaryScale,
						},
					},
				});
			} else {
				const response = await create({
					variables: {
						input: {
							title: formik.values.title,
							description: formik.values.description,
							externalReferenceID: formik.values.externalReferenceID,
							...salaryScale,
						},
					},
				});

				history.push(`/settings/jobs/titles/${response.data?.createJobTitle.jobTitle.id}`);

				complete();
			}

			addToast(t('common.settings.jobTitles.edit.success'), {
				appearance: 'success',
				autoDismiss: true,
			});
		}),
	});

	const loading = createLoading || updateLoading;
	const archiveBusy = archiveLoading || restoreLoading;
	const titleName = props.jobTitle ? localize(props.jobTitle.title).text : undefined;

	return (
		<FlexGap flexDirection="column" gap={tokens.gap.page} width="100%">
			{!isHRBusinessPartner && (
				<FlexGap alignItems="center" justifyContent="space-between" width="100%">
					<Heading mb={0}>
						{t(`common.settings.jobTitles.${props.jobTitle ? 'edit' : 'create'}.title`)}
						{titleName ? (
							<>
								{' “'}
								<strong>{titleName}</strong>
								{'”'}
							</>
						) : (
							''
						)}
					</Heading>
					{props.jobTitle && (
						<Button
							type="secondary"
							icon={<ArchiveIcon />}
							loading={archiveBusy}
							title={props.jobTitle.isArchived ? t('common.restore') : t('common.archive')}
							onClick={attempt(async () => {
								const input = { variables: { input: { id: props.jobTitle!.id } } };
								if (props.jobTitle!.isArchived) {
									await restore(input);
								} else {
									await archive(input);
								}
								history.push('/settings/jobs/titles');
							})}
						/>
					)}
				</FlexGap>
			)}
			<FlexGap flexDirection="column" gap={tokens.gap.section} width="100%">
				<GraphQLErrorMessage error={error} />
				<FormikProvider value={formik}>
					<Form>
						{isHRBusinessPartner && (
							<Box>
								<Flex
									alignItems="center"
									flexWrap="wrap"
									justifyContent="space-between"
									minHeight={64}
								>
									<Box width={[12 / 12, 12 / 12, 3 / 12]} mb={[1, 1, 0]}>
										<Label>{t('common.settings.jobTitles.edit.name')}</Label>
									</Box>

									<Box width={[12 / 12, 12 / 12, 9 / 12]}>{props.jobTitle?.title?.[0]?.text}</Box>
								</Flex>
								<Flex
									alignItems="center"
									flexWrap="wrap"
									justifyContent="space-between"
									minHeight={64}
								>
									<Box width={[12 / 12, 12 / 12, 3 / 12]} mb={[1, 1, 0]}>
										<Label>{t('common.settings.jobTitles.edit.description')}</Label>
									</Box>

									<Box width={[12 / 12, 12 / 12, 9 / 12]}>
										<QuillText value={props.jobTitle?.description?.[0]?.text} />
									</Box>
								</Flex>
								<Flex
									alignItems="center"
									flexWrap="wrap"
									justifyContent="space-between"
									minHeight={64}
								>
									<Box width={[12 / 12, 12 / 12, 3 / 12]} mb={[1, 1, 0]}>
										<Label>{t('common.settings.jobTitles.edit.externalReferenceID')}</Label>
									</Box>

									<Box
										width={[12 / 12, 12 / 12, 9 / 12]}
									>{`${props.jobTitle?.externalReferenceID || '-'}`}</Box>
								</Flex>
								{hasRewards && (
									<Flex
										alignItems="center"
										flexWrap="wrap"
										justifyContent="space-between"
										minHeight={64}
									>
										<Box width={[12 / 12, 12 / 12, 3 / 12]} mb={[1, 1, 0]}>
											<Label>{t('common.settings.jobTitles.edit.salaryScale')}</Label>
										</Box>

										<Box width={[12 / 12, 12 / 12, 9 / 12]}>
											{formatSalaryScale(props.jobTitle?.salaryScale, i18n.language) ?? '-'}
										</Box>
									</Flex>
								)}
							</Box>
						)}
						{!isHRBusinessPartner && (
							<DetailsTableForm
								formValues={[
									{
										label: t('common.settings.jobTitles.edit.name'),
										name: 'title',
										type: DetailsTableFormValueTypes.Multilingual,
									},
									{
										label: t('common.settings.jobTitles.edit.description'),
										name: 'description',
										type: DetailsTableFormValueTypes.MultilingualQuill,
									},
									{
										label: t('common.settings.jobTitles.edit.externalReferenceID'),
										name: 'externalReferenceID',
										type: DetailsTableFormValueTypes.Text,
									},
									...(hasRewards
										? [
												{
													label: t('common.settings.jobTitles.edit.salaryScale'),
													name: 'salaryScaleID',
													type: DetailsTableFormValueTypes.Select,
													search: true,
													options: salaryScaleOptions(
														salaryScalesData?.salaryScales.edges.map(({ node }) => node) ?? [],
														props.jobTitle?.salaryScale,
														i18n.language,
														t('common.none'),
													),
												},
											]
										: []),
								]}
							/>
						)}
					</Form>
				</FormikProvider>

				{props.jobTitle && <KeyPositionToggle jobTitle={props.jobTitle} />}
				{props.jobTitle && !isHRBusinessPartner && (
					<GrowthPathExclusionToggle jobTitle={props.jobTitle} />
				)}
				{!isHRBusinessPartner && (
					<Flex>
						<Button
							role="button"
							aria-label={t('common.save')}
							onClick={() => formik.submitForm()}
							loading={loading}
							disabled={!formik.values.title.length}
							title={t('common.save')}
						/>
					</Flex>
				)}
				{props.jobTitle &&
					!isHRBusinessPartner &&
					(showJobCompetencies || showJobGoalTemplates) && (
						<>
							{showJobCompetencies && <JobCompetenciesEditor jobTitle={props.jobTitle} />}
							{showJobGoalTemplates && <GoalTemplates jobTitle={props.jobTitle} />}
						</>
					)}
			</FlexGap>
		</FlexGap>
	);
}

export default function JobTitleEditorWithID(props: { id: string }) {
	const { data } = graphql.useJobTitleQuery({
		variables: {
			id: props.id,
		},
		fetchPolicy: 'network-only',
	});

	if (!data) {
		return <Spinner centered />;
	}

	return <JobTitleEditor jobTitle={data.jobTitle as graphql.JobTitle} />;
}

function salaryScaleOptions(
	scales: graphql.SalaryScale[],
	current: graphql.SalaryScale | null | undefined,
	locale: string,
	emptyLabel: string,
) {
	const options = scales.map((scale) => ({
		label: formatSalaryScale(scale, locale) ?? scale.name,
		value: scale.id,
	}));

	if (current && !options.some((option) => option.value === current.id)) {
		options.push({
			label: formatSalaryScale(current, locale) ?? current.name,
			value: current.id,
		});
	}

	return [{ label: emptyLabel, value: '' }, ...options];
}
